import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface ExtensionPromptProps {
  variant?: 'banner' | 'card';
}

// Check if running in Chrome on desktop
function isDesktopChrome(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const isChrome = ua.includes('chrome') && !ua.includes('edg/');
  const isDesktop = !/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
  return isChrome && isDesktop;
}

// Check if extension is installed by looking for injected element or global
function isExtensionInstalled(): boolean {
  // The extension could inject a marker element or set a global variable
  // For now, we check for a data attribute that the extension could add
  return document.documentElement.hasAttribute('data-readlater-extension') ||
    (window as unknown as { __READLATER_EXTENSION__?: boolean }).__READLATER_EXTENSION__ === true;
}

const DISMISS_KEY = 'extension-prompt-dismissed';
const DISMISS_UNTIL_KEY = 'extension-prompt-dismiss-until';

export function ExtensionPrompt({ variant = 'banner' }: ExtensionPromptProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if should show prompt
    const shouldShow = () => {
      // Don't show if not desktop Chrome
      if (!isDesktopChrome()) return false;

      // Don't show if extension is installed
      if (isExtensionInstalled()) {
        return false;
      }

      // Check if permanently dismissed
      if (localStorage.getItem(DISMISS_KEY) === 'true') return false;

      // Check if temporarily dismissed
      const dismissUntil = localStorage.getItem(DISMISS_UNTIL_KEY);
      if (dismissUntil && new Date(dismissUntil) > new Date()) return false;

      return true;
    };

    // Small delay to allow extension to inject its marker
    const timer = setTimeout(() => {
      setVisible(shouldShow());
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    // Dismiss for 7 days
    const dismissUntil = new Date();
    dismissUntil.setDate(dismissUntil.getDate() + 7);
    localStorage.setItem(DISMISS_UNTIL_KEY, dismissUntil.toISOString());
  };

  const handleDismissPermanently = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, 'true');
  };

  if (!visible) return null;

  if (variant === 'card') {
    return (
      <div className="extension-prompt-card">
        <button className="prompt-close" onClick={handleDismiss} aria-label="Dismiss">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <div className="prompt-icon">🧩</div>
        <h3>Get the Chrome Extension</h3>
        <p>Save articles with one click while you browse</p>

        <Link to="/download" className="prompt-btn-primary">
          Download Extension
        </Link>

        <button className="prompt-btn-text" onClick={handleDismissPermanently}>
          Don't show again
        </button>

        <style>{`
          .extension-prompt-card {
            background: linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(234, 88, 12, 0.05));
            border: 1px solid rgba(249, 115, 22, 0.2);
            border-radius: 16px;
            padding: 1.5rem;
            text-align: center;
            position: relative;
            margin: 1rem 0;
          }

          .extension-prompt-card .prompt-close {
            position: absolute;
            top: 0.75rem;
            right: 0.75rem;
            background: none;
            border: none;
            color: var(--text-secondary, #888888);
            cursor: pointer;
            padding: 0.25rem;
            border-radius: 4px;
          }

          .extension-prompt-card .prompt-close:hover {
            background: rgba(255, 255, 255, 0.1);
          }

          .extension-prompt-card .prompt-icon {
            font-size: 2rem;
            margin-bottom: 0.75rem;
          }

          .extension-prompt-card h3 {
            margin: 0 0 0.5rem;
            font-size: 1rem;
            font-weight: 600;
          }

          .extension-prompt-card p {
            margin: 0 0 1rem;
            color: var(--text-secondary, #888888);
            font-size: 0.875rem;
          }

          .extension-prompt-card .prompt-btn-primary {
            display: inline-block;
            padding: 0.625rem 1.25rem;
            background: var(--accent, #f97316);
            color: white;
            border-radius: 8px;
            text-decoration: none;
            font-weight: 500;
            font-size: 0.875rem;
            margin-bottom: 0.75rem;
            transition: opacity 0.2s;
          }

          .extension-prompt-card .prompt-btn-primary:hover {
            opacity: 0.9;
          }

          .extension-prompt-card .prompt-btn-text {
            display: block;
            background: none;
            border: none;
            color: var(--text-tertiary, #555555);
            font-size: 0.75rem;
            cursor: pointer;
            margin: 0 auto;
          }

          .extension-prompt-card .prompt-btn-text:hover {
            color: var(--text-secondary, #888888);
          }
        `}</style>
      </div>
    );
  }

  // Banner variant (default)
  return (
    <div className="extension-prompt-banner">
      <div className="prompt-content">
        <span className="prompt-icon">🧩</span>
        <span className="prompt-text">
          <strong>Save articles faster!</strong> Get the Chrome extension for one-click saving.
        </span>
        <Link to="/download" className="prompt-btn">
          Get Extension
        </Link>
      </div>
      <button className="prompt-close" onClick={handleDismiss} aria-label="Dismiss">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>

      <style>{`
        .extension-prompt-banner {
          background: linear-gradient(90deg, rgba(249, 115, 22, 0.15), rgba(234, 88, 12, 0.1));
          border-bottom: 1px solid rgba(249, 115, 22, 0.2);
          padding: 0.625rem 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .extension-prompt-banner .prompt-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
        }

        .extension-prompt-banner .prompt-icon {
          font-size: 1.25rem;
          flex-shrink: 0;
        }

        .extension-prompt-banner .prompt-text {
          font-size: 0.875rem;
          color: var(--text-primary, #ffffff);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .extension-prompt-banner .prompt-text strong {
          color: var(--accent, #f97316);
        }

        .extension-prompt-banner .prompt-btn {
          flex-shrink: 0;
          padding: 0.375rem 0.875rem;
          background: var(--accent, #f97316);
          color: white;
          border-radius: 6px;
          text-decoration: none;
          font-size: 0.8125rem;
          font-weight: 500;
          transition: opacity 0.2s;
        }

        .extension-prompt-banner .prompt-btn:hover {
          opacity: 0.9;
        }

        .extension-prompt-banner .prompt-close {
          background: none;
          border: none;
          color: var(--text-secondary, #888888);
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          flex-shrink: 0;
        }

        .extension-prompt-banner .prompt-close:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        @media (max-width: 600px) {
          .extension-prompt-banner .prompt-text {
            display: none;
          }

          .extension-prompt-banner .prompt-content::after {
            content: 'Get the extension!';
            font-size: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
}

// Hook to check extension status for other components
export function useExtensionStatus() {
  const [status, setStatus] = useState<'unknown' | 'installed' | 'not-installed'>('unknown');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isDesktopChrome()) {
        setStatus('not-installed'); // Not applicable, but treat as not-installed
      } else if (isExtensionInstalled()) {
        setStatus('installed');
      } else {
        setStatus('not-installed');
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  return status;
}
